import { useCallback, useEffect, useState } from "react";
import { LogoDrop } from "@/components/LogoDrop";
import Layout from "@/components/Layout";
import { useSettings } from "@/context/SettingsContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Palette, KeyRound, Loader2, CheckCircle2, XCircle, Save, Plug, MonitorPlay, Share2, Copy, Check, ExternalLink, MessageCircle, Stethoscope } from "lucide-react";

const KEY_FIELDS = [
  { k: "cliniko_api_key", label: "Cliniko API Key", help: "Key incl. shard suffix e.g. ...-uk3", secret: true, test: "cliniko" },
  { k: "cliniko_user_agent", label: "Cliniko User-Agent", help: "App name + contact email", secret: false },
  { k: "rmp_api_key", label: "Rehab My Patient API Key", help: "From RMP → Settings → API", secret: true, test: "rmp" },
  { k: "moti_base_url", label: "Moti Physio Base URL", help: "API base URL (from Moti support)", secret: false },
  { k: "moti_api_key", label: "Moti Physio API Key", help: "Provided by Moti", secret: true, test: "moti" },
  { k: "resend_api_key", label: "Resend API Key (own email domain)", help: "Sends patient emails from info@themedicaltherapygroup.com once your domain is verified in Resend. Test sends a check email to info@.", secret: true, test: "resend" },
];

export default function SettingsPage() {
  const { settings, refresh } = useSettings();
  const [form, setForm] = useState({
    business_name: "", logo_url: "", logo_full_url: "", brand_primary: "#0D9488", tagline: "",
    welcome_headline: "", welcome_subtext: "", welcome_footer: "", email_sign_off: "",
  });
  const [keys, setKeys] = useState({});
  const [ks, setKs] = useState({});
  const [tests, setTests] = useState({});
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState("");
  const [reco, setReco] = useState({ reco_gap_days: 30, reco_long_type_id: "", reco_short_type_id: "" });
  const [recoTypes, setRecoTypes] = useState([]);
  const [savingReco, setSavingReco] = useState(false);

  const bookUrl = `${window.location.origin}/book`;
  const brand = form.brand_primary || "#0D9488";
  const btnLabel = "Book with AI Receptionist";
  const htmlSnippet = `<a href="${bookUrl}" target="_blank" rel="noopener" style="display:inline-block;background:${brand};color:#ffffff;font-family:system-ui,-apple-system,sans-serif;font-weight:600;font-size:15px;line-height:1;padding:14px 24px;border-radius:9999px;text-decoration:none;">${btnLabel}</a>`;
  const waMessage = `Hi! You can book an appointment with our AI Receptionist here: ${bookUrl}`;
  const waShareUrl = `https://wa.me/?text=${encodeURIComponent(waMessage)}`;
  const bubbleSnippet = `<a href="${bookUrl}" target="_blank" rel="noopener" style="position:fixed;bottom:24px;right:24px;z-index:2147483000;display:inline-flex;align-items:center;gap:8px;background:${brand};color:#ffffff;font-family:system-ui,-apple-system,sans-serif;font-weight:600;font-size:15px;padding:14px 20px;border-radius:9999px;text-decoration:none;box-shadow:0 10px 30px rgba(0,0,0,0.2)"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>Book with AI Receptionist</a>`;

  const copy = async (id, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(""), 1800);
    } catch { toast.error("Could not copy — select and copy manually"); }
  };

  useEffect(() => {
    setForm({
      business_name: settings.business_name || "", logo_url: settings.logo_url || "",
      logo_full_url: settings.logo_full_url || "",
      brand_primary: settings.brand_primary || "#0D9488", tagline: settings.tagline || "Clinical Workspace",
      welcome_headline: settings.welcome_headline || "", welcome_subtext: settings.welcome_subtext || "",
      welcome_footer: settings.welcome_footer || "Clinical Dashboard",
      email_sign_off: settings.email_sign_off || "",
    });
  }, [settings]);

  const loadKeys = useCallback(() => api.get("/settings").then((r) => {
    setKs(r.data.keys_set || {});
    setReco({ reco_gap_days: r.data.reco_gap_days ?? 30, reco_long_type_id: r.data.reco_long_type_id || "", reco_short_type_id: r.data.reco_short_type_id || "" });
  }).catch(() => {}), []);
  useEffect(() => { loadKeys(); }, [loadKeys]);
  useEffect(() => { api.get("/receptionist/config").then((r) => setRecoTypes(r.data.appointment_types || [])).catch(() => {}); }, []);

  const saveReco = async () => {
    setSavingReco(true);
    try {
      await api.put("/settings", { reco_gap_days: Number(reco.reco_gap_days) || 30, reco_long_type_id: reco.reco_long_type_id, reco_short_type_id: reco.reco_short_type_id });
      toast.success("Receptionist recommendation settings saved");
    } catch { toast.error("Could not save recommendation settings"); }
    finally { setSavingReco(false); }
  };

  const saveBranding = async () => {
    setSaving(true);
    try { await api.put("/settings", form); await refresh(); toast.success("Branding & welcome screen updated"); }
    catch { toast.error("Could not save branding"); }
    finally { setSaving(false); }
  };

  const saveKeys = async () => {
    const payload = Object.fromEntries(Object.entries(keys).filter(([, v]) => v !== undefined && v !== ""));
    if (Object.keys(payload).length === 0) { toast.error("Enter at least one key to save"); return; }
    setSaving(true);
    try {
      await api.put("/settings", payload);
      await loadKeys();
      setKeys({});
      toast.success("API keys saved — they apply immediately. Run Sync to pull data.");
    } catch { toast.error("Could not save keys"); }
    finally { setSaving(false); }
  };

  const testSvc = async (svc) => {
    setTests((t) => ({ ...t, [svc]: { loading: true } }));
    try {
      const { data } = await api.post(`/settings/test/${svc}`);
      setTests((t) => ({ ...t, [svc]: { loading: false, ok: data.ok, msg: data.message } }));
    } catch {
      setTests((t) => ({ ...t, [svc]: { loading: false, ok: false, msg: "Test failed" } }));
    }
  };

  return (
    <Layout title="Settings" subtitle="Branding, welcome screen and integration keys — changes apply without redeploy">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Branding */}
        <div className="space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center gap-2 text-sm font-bold text-slate-800"><Palette className="h-4 w-4 text-teal-600" /> Branding</div>
            <div className="space-y-3">
              <div><Label className="text-xs text-slate-500">Business name</Label>
                <Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="settings-business-name" className="mt-1" /></div>
              <div><Label className="text-xs text-slate-500">Tagline (shown under the logo)</Label>
                <Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} data-testid="settings-tagline" className="mt-1" /></div>
              <div><Label className="text-xs text-slate-500">Email sign-off name</Label>
                <Input value={form.email_sign_off} onChange={(e) => setForm({ ...form, email_sign_off: e.target.value })} data-testid="settings-email-signoff" className="mt-1" placeholder="e.g. Ahmet" />
                <p className="mt-0.5 text-[10px] text-slate-400">Signs patient emails as this name, with your business name underneath.</p></div>

              <LogoDrop label="Logo (icon) — drag & drop a file" help="Small square mark used in the sidebar and app header."
                value={form.logo_url} onChange={(v) => setForm((f) => ({ ...f, logo_url: v }))} testid="settings-logo" />
              <LogoDrop label="Full logo (with wordmark)" help="Wide logo used on PDF treatment plans and patient documents. Falls back to the icon logo if empty."
                value={form.logo_full_url} onChange={(v) => setForm((f) => ({ ...f, logo_full_url: v }))} testid="settings-full-logo" />

              <div className="flex items-center gap-3">
                <div className="flex-1"><Label className="text-xs text-slate-500">Brand colour</Label>
                  <Input value={form.brand_primary} onChange={(e) => setForm({ ...form, brand_primary: e.target.value })} data-testid="settings-brand-color" className="mt-1" /></div>
                <input type="color" value={form.brand_primary} onChange={(e) => setForm({ ...form, brand_primary: e.target.value })} className="mt-5 h-10 w-12 cursor-pointer rounded-lg border border-slate-200" />
              </div>
            </div>
          </div>

          {/* Welcome / login screen */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center gap-2 text-sm font-bold text-slate-800"><MonitorPlay className="h-4 w-4 text-teal-600" /> Welcome / login screen</div>
            <div className="space-y-3">
              <div><Label className="text-xs text-slate-500">Headline</Label>
                <Input value={form.welcome_headline} onChange={(e) => setForm({ ...form, welcome_headline: e.target.value })} data-testid="settings-welcome-headline" className="mt-1" placeholder="Clinical decision support & patient retention." /></div>
              <div><Label className="text-xs text-slate-500">Sub-text</Label>
                <textarea value={form.welcome_subtext} onChange={(e) => setForm({ ...form, welcome_subtext: e.target.value })} data-testid="settings-welcome-subtext" rows={3}
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500" placeholder="Short paragraph shown on the welcome screen" /></div>
              <div><Label className="text-xs text-slate-500">Footer line</Label>
                <Input value={form.welcome_footer} onChange={(e) => setForm({ ...form, welcome_footer: e.target.value })} data-testid="settings-welcome-footer" className="mt-1" placeholder="Clinical Dashboard" /></div>
            </div>
            <Button onClick={saveBranding} disabled={saving} data-testid="settings-save-branding" className="mt-4 w-full bg-teal-600 hover:bg-teal-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" /> Save branding & welcome screen</>}
            </Button>
          </div>
        </div>

        {/* Integrations */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center gap-2 text-sm font-bold text-slate-800"><KeyRound className="h-4 w-4 text-teal-600" /> Integration keys</div>
          <div className="space-y-3">
            {KEY_FIELDS.map((f) => {
              const r = f.test ? tests[f.test] : null;
              return (
                <div key={f.k}>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-slate-500">{f.label}</Label>
                    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold ${ks[f.k] ? "text-emerald-600" : "text-slate-400"}`}>
                      {ks[f.k] ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}{ks[f.k] ? "Set" : "Not set"}
                    </span>
                  </div>
                  <div className="mt-1 flex gap-2">
                    <Input type={f.secret ? "password" : "text"} value={keys[f.k] ?? ""} onChange={(e) => setKeys({ ...keys, [f.k]: e.target.value })}
                      data-testid={`settings-${f.k}`} className="flex-1" placeholder={ks[f.k] ? "•••••• (leave blank to keep)" : f.help} />
                    {f.test && (
                      <Button type="button" variant="outline" onClick={() => testSvc(f.test)} disabled={r?.loading} data-testid={`settings-test-${f.test}`} className="flex-shrink-0">
                        {r?.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plug className="mr-1 h-3.5 w-3.5" /> Test</>}
                      </Button>
                    )}
                  </div>
                  {r && !r.loading && (
                    <p className={`mt-1 flex items-center gap-1 text-[11px] ${r.ok ? "text-emerald-600" : "text-red-500"}`} data-testid={`settings-test-result-${f.test}`}>
                      {r.ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}{r.msg}
                    </p>
                  )}
                  {!r && <p className="mt-0.5 text-[10px] text-slate-400">{f.help}</p>}
                </div>
              );
            })}
          </div>
          <Button onClick={saveKeys} disabled={saving} data-testid="settings-save-keys" className="mt-4 w-full bg-teal-600 hover:bg-teal-700">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" /> Save keys</>}
          </Button>
          <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-[11px] text-slate-500">Keys are stored server-side and never shown back. Save first, then click <b>Test</b>. After saving Cliniko, use <b>Sync all</b> to pull your entire patient list.</p>
        </div>
      </div>

      {/* Book Now snippet generator */}
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="settings-book-now-card">
        <div className="mb-1 flex items-center gap-2 text-sm font-bold text-slate-800"><Share2 className="h-4 w-4 text-teal-600" /> "Book Now" button &amp; links</div>
        <p className="mb-4 text-[11px] text-slate-500">Share your AI Receptionist so patients can book themselves. These point to your live public booking page — no login needed.</p>

        {/* Shareable link */}
        <div className="mb-5">
          <Label className="text-xs font-semibold text-slate-600">Public booking link</Label>
          <div className="mt-1.5 flex gap-2">
            <Input readOnly value={bookUrl} data-testid="book-link-input" className="flex-1 bg-slate-50 font-mono text-xs" onFocus={(e) => e.target.select()} />
            <Button type="button" variant="outline" onClick={() => copy("link", bookUrl)} data-testid="copy-book-link" className="flex-shrink-0">
              {copied === "link" ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
            </Button>
            <a href={bookUrl} target="_blank" rel="noopener noreferrer" data-testid="open-book-link">
              <Button type="button" variant="outline" className="flex-shrink-0"><ExternalLink className="h-4 w-4" /></Button>
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          {/* Website embed */}
          <div>
            <Label className="text-xs font-semibold text-slate-600">Website button (HTML embed)</Label>
            <p className="mb-2 mt-0.5 text-[10px] text-slate-400">Paste this into your website where you want the button to appear.</p>
            <div className="mb-3 flex items-center justify-center rounded-xl border border-slate-200 bg-slate-50 p-5">
              <a href={bookUrl} target="_blank" rel="noopener noreferrer" data-testid="book-button-preview"
                style={{ display: "inline-block", background: brand, color: "#fff", fontWeight: 600, fontSize: 15, lineHeight: 1, padding: "14px 24px", borderRadius: 9999, textDecoration: "none" }}>
                {btnLabel}
              </a>
            </div>
            <textarea readOnly value={htmlSnippet} data-testid="book-html-snippet" rows={4} onFocus={(e) => e.target.select()}
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-[11px] text-slate-700 focus:border-teal-500 focus:outline-none" />
            <Button type="button" variant="outline" onClick={() => copy("html", htmlSnippet)} data-testid="copy-book-html" className="mt-2 w-full">
              {copied === "html" ? <><Check className="mr-1.5 h-4 w-4 text-emerald-600" /> Copied</> : <><Copy className="mr-1.5 h-4 w-4" /> Copy embed code</>}
            </Button>
          </div>

          {/* WhatsApp */}
          <div>
            <Label className="text-xs font-semibold text-slate-600">WhatsApp link</Label>
            <p className="mb-2 mt-0.5 text-[10px] text-slate-400">Send patients a ready-to-share message with your booking link.</p>
            <textarea readOnly value={waMessage} data-testid="book-wa-message" rows={4} onFocus={(e) => e.target.select()}
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] text-slate-700 focus:border-teal-500 focus:outline-none" />
            <div className="mt-2 flex gap-2">
              <Button type="button" variant="outline" onClick={() => copy("wa", waMessage)} data-testid="copy-book-wa" className="flex-1">
                {copied === "wa" ? <><Check className="mr-1.5 h-4 w-4 text-emerald-600" /> Copied</> : <><Copy className="mr-1.5 h-4 w-4" /> Copy message</>}
              </Button>
              <a href={waShareUrl} target="_blank" rel="noopener noreferrer" data-testid="share-book-wa" className="flex-1">
                <Button type="button" className="w-full bg-[#25D366] hover:bg-[#1eb855]"><MessageCircle className="mr-1.5 h-4 w-4" /> Share on WhatsApp</Button>
              </a>
            </div>
          </div>
        </div>

        {/* Floating chat bubble */}
        <div className="mt-5 border-t border-slate-100 pt-5">
          <Label className="text-xs font-semibold text-slate-600">Floating "Book" bubble (any page)</Label>
          <p className="mb-2 mt-0.5 text-[10px] text-slate-400">Paste once before your site's closing &lt;/body&gt; tag — it floats in the bottom-right corner on every page.</p>
          <div className="relative mb-3 h-28 overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
            <a href={bookUrl} target="_blank" rel="noopener noreferrer" data-testid="book-bubble-preview"
              style={{ position: "absolute", bottom: 16, right: 16, display: "inline-flex", alignItems: "center", gap: 8, background: brand, color: "#fff", fontWeight: 600, fontSize: 14, padding: "12px 18px", borderRadius: 9999, textDecoration: "none", boxShadow: "0 10px 30px rgba(0,0,0,0.2)" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
              Book with AI Receptionist
            </a>
          </div>
          <textarea readOnly value={bubbleSnippet} data-testid="book-bubble-snippet" rows={4} onFocus={(e) => e.target.select()}
            className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-[11px] text-slate-700 focus:border-teal-500 focus:outline-none" />
          <Button type="button" variant="outline" onClick={() => copy("bubble", bubbleSnippet)} data-testid="copy-book-bubble" className="mt-2 w-full">
            {copied === "bubble" ? <><Check className="mr-1.5 h-4 w-4 text-emerald-600" /> Copied</> : <><Copy className="mr-1.5 h-4 w-4" /> Copy floating bubble code</>}
          </Button>
        </div>
      </div>
      {/* Receptionist recommendation tuning */}
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="settings-reco-card">
        <div className="mb-1 flex items-center gap-2 text-sm font-bold text-slate-800"><Stethoscope className="h-4 w-4 text-teal-600" /> AI Receptionist recommendations</div>
        <p className="mb-4 text-[11px] text-slate-500">Tune how the receptionist suggests appointment length based on time since a patient's last visit.</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <Label className="text-xs text-slate-500">Gap threshold (days)</Label>
            <Input type="number" min={1} value={reco.reco_gap_days} onChange={(e) => setReco({ ...reco, reco_gap_days: e.target.value })} data-testid="settings-reco-gap" className="mt-1" />
            <p className="mt-0.5 text-[10px] text-slate-400">More than this since last visit → suggest the longer appointment.</p>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Longer / new-patient type</Label>
            <Select value={reco.reco_long_type_id || "auto"} onValueChange={(v) => setReco({ ...reco, reco_long_type_id: v === "auto" ? "" : v })}>
              <SelectTrigger data-testid="settings-reco-long" className="mt-1"><SelectValue placeholder="Auto (longest)" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="auto">Auto (pick longest)</SelectItem>
                {recoTypes.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}{t.duration ? ` · ${t.duration}m` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Recent / follow-up type</Label>
            <Select value={reco.reco_short_type_id || "auto"} onValueChange={(v) => setReco({ ...reco, reco_short_type_id: v === "auto" ? "" : v })}>
              <SelectTrigger data-testid="settings-reco-short" className="mt-1"><SelectValue placeholder="Auto (shortest)" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="auto">Auto (pick shortest)</SelectItem>
                {recoTypes.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}{t.duration ? ` · ${t.duration}m` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        {recoTypes.length === 0 && <p className="mt-3 text-[11px] text-amber-600">Connect Cliniko to load your appointment types here.</p>}
        <Button onClick={saveReco} disabled={savingReco} data-testid="settings-save-reco" className="mt-4 bg-teal-600 hover:bg-teal-700">
          {savingReco ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" /> Save recommendation settings</>}
        </Button>
      </div>
    </Layout>
  );
}
